/* eslint-disable no-unused-expressions */
import React, { useState, useEffect, BaseSyntheticEvent } from 'react';
import MarkerSearchBox from 'components/formula-entry/marker-search-box';
import history from 'lib/history';
import { ModalContainerDark, OptionModal } from 'components/modal-provider/modal';
import {
  ButtonContainer,
  CancelButton,
  CloseIcon,
  Container,
  ContentGroup,
  Label,
  NoDealsMessage,
  ParetoModalContent,
  ParetoModalContentLeft,
  ParetoModalContentRight,
  SaveFormulaButton,
  Title,
  TitleContainer,
} from 'components/formula-entry/modals/_common';
import Select from 'components/input/select';
import MenuItem from 'components/menu-item';
import { IDeal } from 'store/deals/types';
import { EParetoDealFilter } from 'store/pareto/types';
import { SearchHeader, SelectContainer } from 'pages/compare/components';
import { removeUserGroup } from 'lib/helpers/name-formatters';
import { TParetoAddDealModalProps } from 'pages/pareto/add-deal-modal';
import ResultListToggle from 'components/result-list-toggle';
import ResultListToggleItem from 'components/result-list-toggle-item';
import { EModalTitle } from 'lib/storage';

function paretoPortfolioValidate(urlText: string) {
  return urlText.lastIndexOf('/pareto/') !== -1 || urlText.lastIndexOf('/portfolio/') !== -1;
}

function dealIdUtil(deal: IDeal, urlDealIds: string[], dealIds: string[]) {
  if (deal.id && urlDealIds.includes(deal.id)) {
    dealIds.push(deal.id);
  }
}

function handleAllButtonClick(
  filteredDeals: IDeal[],
  isAllButtonSelected: boolean,
  setDealIdsToAdd: Function,
  setIsAllButtonSelected: Function
) {
  if (isAllButtonSelected) {
    setIsAllButtonSelected(false);
    setDealIdsToAdd([]);
  } else {
    setIsAllButtonSelected(true);
    const allDealIds: string[] = [];
    filteredDeals.forEach(filteredDeal => {
      const { id } = filteredDeal;
      if (id) {
        allDealIds.push(id);
      }
    });
    setDealIdsToAdd(allDealIds);
  }
}

function toggleDealSelect(dealIdsToAdd: string[], setDealIdsToAdd: Function, dealId?: string) {
  if (dealId === undefined) {
    return;
  }
  let indexFound = -1;
  dealIdsToAdd.some((dealIdToAdd, index) => {
    if (dealIdToAdd === dealId) {
      indexFound = index;
      return true;
    }
    return false;
  });
  const dealIdsToAddUpdated = [...dealIdsToAdd];
  indexFound > -1 ? dealIdsToAddUpdated.splice(indexFound, 1) : dealIdsToAddUpdated.push(dealId);
  setDealIdsToAdd(dealIdsToAddUpdated);
}

function handleAddToChart(
  dealIdsToAdd: string[],
  setParetoDealIds: Function,
  setPortfolioDealIds: Function,
  setParetoIsModalOpen: Function,
  modalTitle: EModalTitle
) {
  if (!dealIdsToAdd.length) {
    setParetoIsModalOpen(false, modalTitle);
    return;
  }
  if (window.location.href.lastIndexOf('/portfolio') !== -1) {
    setPortfolioDealIds(dealIdsToAdd);
    // Newly added to handle bookmarked pages by the user
    history.push(`${'/portfolio'}/${dealIdsToAdd.join()}`);
    return;
  }
  setParetoDealIds(dealIdsToAdd);
  // Newly added to handle bookmarked pages by the user
  history.push(`${'/pareto'}/${dealIdsToAdd.join()}`);
}

export default ({
  availableProductLines,
  availableRegions,
  isModalOpen,
  setParetoSearchKeyword,
  setParetoIsModalOpen,
  searchKeyword,
  setParetoDealFilter,
  setParetoDealIds,
  setParetoProductLineFilter,
  setParetoRegionFilter,
  dealFilter,
  regionFilter,
  productLineFilter,
  filteredDeals,
  paretoDealIds,
  portfolioDealIds,
  setPortfolioDealIds,
  modalTitle,
}: TParetoAddDealModalProps) => {
  const [isAllButtonSelected, setIsAllButtonSelected] = useState(false);
  const [dealIdsToAdd, setDealIdsToAdd] = useState<string[]>(
    modalTitle === EModalTitle.PARETO ? paretoDealIds : portfolioDealIds
  );

  useEffect(() => {
    const urlText: string = window.location.href;
    if (paretoPortfolioValidate(urlText)) {
      const urlDealIds: string[] = urlText
        .substring(urlText.lastIndexOf('/') + 1)
        .toString()
        .split(',');
      const dealIds: string[] = [];
      filteredDeals.forEach((deal: IDeal) => {
        dealIdUtil(deal, urlDealIds, dealIds);
      });
      setDealIdsToAdd(dealIds);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  function handleSearchKeywordChange(e: React.ChangeEvent<HTMLInputElement>) {
    const val = e.target.value;
    setParetoSearchKeyword(val);
  }

  function handleSearchKeywordClear() {
    setParetoSearchKeyword('');
  }

  function formatDealFields(deal: IDeal): string {
    const { name, createdBy, customer } = deal;
    return `${name} / ${customer} / ${removeUserGroup(createdBy)}`;
  }

  if (!isModalOpen) {
    return null;
  }

  return (
    <ModalContainerDark>
      <OptionModal>
        <Container>
          <TitleContainer>
            <Title>Select Deals for {modalTitle}</Title>
            <CloseIcon onClick={() => setParetoIsModalOpen(false, modalTitle)} />
          </TitleContainer>

          <ParetoModalContent>
            <ParetoModalContentLeft>
              <ContentGroup>
                <Label>DEALS</Label>
                <SelectContainer>
                  <Select
                    data-testid="tid-pareto-deal-filter-dropdown"
                    value={dealFilter}
                    onChange={(e: BaseSyntheticEvent) => setParetoDealFilter(e.target.value)}
                  >
                    <MenuItem value={EParetoDealFilter.ALL}>All</MenuItem>
                    <MenuItem value={EParetoDealFilter.MINE}>Mine</MenuItem>
                  </Select>
                </SelectContainer>
              </ContentGroup>
              <ContentGroup>
                <Label>REGION</Label>
                <SelectContainer>
                  <Select
                    data-testid="tid-pareto-region-filter-dropdown"
                    value={regionFilter}
                    onChange={(e: BaseSyntheticEvent) => setParetoRegionFilter(e.target.value)}
                  >
                    <MenuItem key="region-all" value="all">
                      All
                    </MenuItem>
                    {availableRegions.map(availableRegion => (
                      <MenuItem
                        data-testid="tid-pareto-region-filter-dropdown-item"
                        key={`region-${availableRegion.name}`}
                        value={availableRegion.name}
                      >
                        {availableRegion.displayName}
                      </MenuItem>
                    ))}
                  </Select>
                </SelectContainer>
              </ContentGroup>
              <ContentGroup>
                <Label>PRODUCT LINES</Label>
                <SelectContainer>
                  <Select
                    data-testid="tid-pareto-product-line-filter-dropdown"
                    value={productLineFilter}
                    onChange={(e: BaseSyntheticEvent) => setParetoProductLineFilter(e.target.value)}
                  >
                    <MenuItem key="product-line-all" value="all">
                      All
                    </MenuItem>
                    {availableProductLines.map(availableProductLine => (
                      <MenuItem key={`product-line-${availableProductLine.name}`} value={availableProductLine.name}>
                        {availableProductLine.displayName}
                      </MenuItem>
                    ))}
                  </Select>
                </SelectContainer>
              </ContentGroup>
            </ParetoModalContentLeft>
            <ParetoModalContentRight>
              <Label>SEARCH</Label>
              <SearchHeader>
                <MarkerSearchBox
                  value={searchKeyword}
                  onChange={handleSearchKeywordChange}
                  onClear={handleSearchKeywordClear}
                />
              </SearchHeader>

              <ResultListToggle>
                {filteredDeals.length ? (
                  <>
                    <ResultListToggleItem
                      description="All"
                      selected={isAllButtonSelected}
                      onClick={() =>
                        handleAllButtonClick(
                          filteredDeals,
                          isAllButtonSelected,
                          setDealIdsToAdd,
                          setIsAllButtonSelected
                        )
                      }
                    />
                    {filteredDeals.map(f => {
                      const checked = dealIdsToAdd.some(did => did === f.id);
                      return (
                        <ResultListToggleItem
                          key={`deal-${f.id}`}
                          description={formatDealFields(f)}
                          selected={checked}
                          onClick={() => toggleDealSelect(dealIdsToAdd, setDealIdsToAdd, f.id)}
                        />
                      );
                    })}
                  </>
                ) : (
                  <NoDealsMessage>No deals</NoDealsMessage>
                )}
              </ResultListToggle>
            </ParetoModalContentRight>
          </ParetoModalContent>

          <ButtonContainer>
            <CancelButton onClick={() => setParetoIsModalOpen(false, modalTitle)}>Cancel</CancelButton>
            <SaveFormulaButton
              data-testid="tid-pareto-add-to-compare-button"
              disabled={!dealIdsToAdd.length}
              onClick={() =>
                handleAddToChart(dealIdsToAdd, setParetoDealIds, setPortfolioDealIds, setParetoIsModalOpen, modalTitle)
              }
            >
              Add to {modalTitle}
            </SaveFormulaButton>
          </ButtonContainer>
        </Container>
      </OptionModal>
    </ModalContainerDark>
  );
};
